import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { getPublishGuardrails } from '../lib/publishGuardrails';
import { ArrowLeft, Plus, X, Upload, Layers, Link, Send, Check, AlertCircle, DollarSign, Bookmark, Info } from 'lucide-react';
import { PaymentProviderLogo, getProviderFromMethod } from '../components/PaymentProviderLogo';
import SafeImage from '../components/SafeImage';
import StaffAccessWindow from '../components/event-form/StaffAccessWindow';
import ValidationSummary from '../components/event-form/ValidationSummary';
import SmartEntryFlowSection from '../components/create-event/SmartEntryFlowSection';
import SourceOptionsSection from '../components/create-event/SourceOptionsSection';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_METHODS = ['Venmo', 'Zelle', 'PayPal', 'Cash App', 'Stripe', 'Other'];

export default function EditEvent({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  // Stabilization guardrails (frontend-only, non-blocking):
  const [freePricingBannerDismissed, setFreePricingBannerDismissed] = useState(false);
  const [showPublishGuardrailsModal, setShowPublishGuardrailsModal] = useState(false);
  // Phase 2F+2G: per-session dismissed guardrail SIGNATURES — resets on event id
  // change / unmount / reload. Signatures embed the underlying condition values
  // (e.g., description length, tier count) so changing the event re-fires the
  // guardrail naturally without needing extra state-watch logic.
  const [dismissedGuardrailSignatures, setDismissedGuardrailSignatures] = useState(() => new Set());
  // Phase 2G: chip popover open state
  const [showDismissedPopover, setShowDismissedPopover] = useState(false);
  // Cache of {signature → title} for chip listing — populated as the host
  // dismisses items so we can list them by title in the popover.
  const [dismissedTitlesBySig, setDismissedTitlesBySig] = useState(() => ({}));
  // Phase 2D: Personal tier preset save modal
  const [showSavePresetModal, setShowSavePresetModal] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [savingPreset, setSavingPreset] = useState(false);
  // P2.3B: Stripe Connect publish guardrail — paid events cannot be published
  // unless host has completed Stripe onboarding. Frontend-only check; backend
  // /api/events/{id} update is NOT modified (see CRITICAL RULES in spec).
  const [showStripeConnectModal, setShowStripeConnectModal] = useState(false);
  const [stripeCheckBusy, setStripeCheckBusy] = useState(false);
  // Iter126: host_defaults for SourceOptionsSection's "Save as default".
  // Optional — best-effort fetch; failure is non-fatal (the save-default
  // button just won't have a baseline merge target). EditEvent retention
  // (iter121) is unaffected because we read host_defaults INTO state, never
  // overwrite event fields with it.
  const [savedHostDefaults, setSavedHostDefaults] = useState({});
  // Allowlist mirrored from the backend — keep frontend & server in lockstep
  const PRESET_TIER_ALLOWLIST = useState(() => new Set([
    'General Access', 'Couples Access', 'Single Female Access', 'Single Male Access',
    'General Admission', 'VIP Access', 'VIP Seating', 'Group Pass', 'Early Bird',
    'Invite Access',
  ]))[0];

  useEffect(() => { fetchEvent(); }, [id]);

  // Iter126: best-effort fetch of host_defaults for the
  // SourceOptionsSection "Save as default" round-trip. Non-fatal on failure.
  useEffect(() => {
    const token = localStorage.getItem('session_token');
    if (!token) return;
    axios
      .get(`${API}/users/host-defaults`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setSavedHostDefaults(r.data || {}))
      .catch(() => { /* non-fatal */ });
  }, []);

  const fetchEvent = async () => {
    try {
      const token = localStorage.getItem('session_token');
      // Use the host-authorized /edit endpoint so private_location_details,
      // real location, and unmasked visibility round-trip correctly. The
      // public /events/{id} endpoint masks these, which would silently wipe
      // them on save.
      const response = await axios.get(`${API}/events/${id}/edit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      // Migrate legacy add_ons into pass_tiers with type="addon"
      if (data.add_ons && data.add_ons.length > 0) {
        const migratedAddons = data.add_ons.map(ao => ({
          id: ao.id,
          tier_name: ao.name,
          price: ao.price,
          description: ao.description || '',
          quantity_available: ao.quantity_available || 0,
          attendees_per_pass: 1,
          is_comp: false,
          status_default: ao.status || 'active',
          requires_access_code: false,
          access_code_hash: '',
          type: 'addon',
          display_order: (data.pass_tiers || []).length + 1
        }));
        data.pass_tiers = [...(data.pass_tiers || []), ...migratedAddons];
        delete data.add_ons;
      }
      // Ensure all tiers have type field
      (data.pass_tiers || []).forEach(t => { if (!t.type) t.type = 'entry'; });

      // Phase 2C: if the host came from Guided Builder with an applied
      // description, pre-fill the Description textarea ONLY if the event
      // doesn't already have a description (never overwrite). One-shot:
      // consume the router state so refreshes don't keep re-applying.
      // Fallback to window.history.state?.usr because ProtectedRoute wraps
      // this component and `useLocation().state` is occasionally stale on
      // first mount while auth resolves.
      try {
        const routerState = location?.state?.guidedDescription;
        const historyState = (typeof window !== 'undefined') ? window.history?.state?.usr?.guidedDescription : null;
        const pending = routerState || historyState;
        if (pending && typeof pending === 'string' && !(data.description || '').trim()) {
          data.description = pending;
          // Clear both so a refresh doesn't keep applying it
          if (typeof window !== 'undefined' && window.history?.replaceState) {
            window.history.replaceState({ usr: {} }, '');
          }
        }
      } catch (_) {
        // non-fatal
      }

      setForm(data);
    } catch (error) {
      toast.error('Failed to fetch event');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateTier = (idx, field, value) => {
    const t = [...form.pass_tiers];
    t[idx] = { ...t[idx], [field]: value };
    if (field === 'is_comp' && value) t[idx].price = 0;
    if (field === 'type' && value === 'addon') {
      t[idx].requires_access_code = false;
      t[idx].access_code_hash = '';
    }
    setForm({ ...form, pass_tiers: t });
  };

  const addPaymentLink = () => {
    setForm({ ...form, payment_links: [...(form.payment_links || []), { id: `pl_${Date.now()}`, method: '', payment_method_type: 'link', url: '', recipient_name: '', email_or_phone: '', qr_code_image: '', suggested_memo: '', instructions: '' }] });
  };

  const updatePaymentLink = (index, field, value) => {
    const updated = [...(form.payment_links || [])];
    if (!updated[index]) return;
    updated[index][field] = value;
    setForm({ ...form, payment_links: updated });
  };

  const removePaymentLink = (index) => {
    setForm({ ...form, payment_links: (form.payment_links || []).filter((_, i) => i !== index) });
  };

  const handlePaymentQRUpload = async (file, index) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/upload`, formData, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } });
      updatePaymentLink(index, 'qr_code_image', response.data.url);
      toast.success('QR code uploaded');
    } catch { toast.error('Failed to upload QR code'); }
  };

  const handleFileUpload = async (file, type) => {
    const uploadFn = type === 'logo' ? setUploadingLogo : setUploadingImage;
    uploadFn(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('session_token');
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, [type === 'logo' ? 'organizer_logo_url' : 'event_image_url']: response.data.url }));
      toast.success(`${type === 'logo' ? 'Organizer Logo' : 'Event Flyer'} uploaded`);
    } catch { toast.error(`Failed to upload ${type}`); }
    finally { uploadFn(false); }
  };

  const invalidToastShownRef = React.useRef(false);
  const formRef = React.useRef(null);
  const [validationNonce, setValidationNonce] = React.useState(0);

  // Build the real save payload and PUT to the backend.
  // Separated from `handleSubmit` so the "Publish Anyway" modal CTA can
  // reuse the exact same code path without going through form validation.
  const _doSaveEvent = async () => {
    const paymentLinks = Array.isArray(form.payment_links) ? form.payment_links : [];
    if (paymentLinks.length === 0) {
      toast.error('Please add at least one payment method');
      return;
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const payload = {
        ...form,
        capacity: form.is_unlimited_capacity ? 0 : (parseInt(form.capacity) || 0),
        is_unlimited_capacity: !!form.is_unlimited_capacity,
        convenience_fee: 0,
        pass_tiers: (form.pass_tiers || []).map((t, i) => ({
          ...t,
          price: parseFloat(t.price) || 0,
          quantity_available: parseInt(t.quantity_available) || 0,
          attendees_per_pass: t.type === 'addon' ? 1 : (parseInt(t.attendees_per_pass) || 1),
          display_order: i,
          allow_quantity_selection: t.allow_quantity_selection !== false,
          min_quantity: t.min_quantity ? parseInt(t.min_quantity) : null,
          max_quantity: t.max_quantity ? parseInt(t.max_quantity) : null
        })),
        payment_links: paymentLinks,
        release_method: form.release_method || 'automatic',
        notification_schedule: form.notification_schedule || 'instant',
      };
      const res = await axios.put(`${API}/events/${id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Event updated');
      const savedId = res?.data?.event_id || id;
      if (savedId) {
        navigate(`/events/${savedId}/manage`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d?.msg || JSON.stringify(d)).join('; ')
          : (error?.message || 'Failed to update event');
      toast.error(msg);
      // eslint-disable-next-line no-console
      console.error('[EditEvent] save failed', { status: error?.response?.status, detail, error });
    } finally {
      setSaving(false);
    }
  };

  // Phase 2F+2G: reset dismissed guardrails when event id changes
  useEffect(() => {
    setDismissedGuardrailSignatures(new Set());
    setDismissedTitlesBySig({});
    setShowDismissedPopover(false);
  }, [id]);

  const handleSubmit = async (e) => {
    invalidToastShownRef.current = false;
    e.preventDefault();

    // P2.3B: Stripe Connect publish guardrail. Runs BEFORE other guardrails
    // so we never let a paid event slip through into the publish path
    // without a connected Stripe account. Free events skip this entirely.
    //
    // CRITICAL — DO NOT REMOVE THE `usesStripeCheckout` CHECK.
    // Both conditions MUST be true for the modal to fire:
    //   hasPaidTier && usesStripeCheckout
    //
    // Why:
    //   Paid events that use ONLY direct/manual payment methods
    //   (Venmo, Zelle, Cash App, PayPal, Other) are 100% valid without
    //   Stripe Connect onboarding — they never touch Stripe. Forcing a
    //   Stripe onboarding modal on those events blocks legitimate edits
    //   to legacy events and was the iter124 production regression.
    //   Stripe Connect onboarding is required ONLY when the event uses
    //   Stripe Checkout as a payment method.
    //
    // If you ever add another paid-rail (e.g. Square, Razorpay), gate
    // them on their OWN onboarding state — do not piggy-back on this
    // Stripe-specific check.
    const hasPaidTier = (form?.pass_tiers || []).some(
      (t) => !t?.is_comp && parseFloat(t?.price || 0) > 0
    );
    const usesStripeCheckout = (form?.payment_links || []).some(
      (pl) => (pl?.method || '').toLowerCase() === 'stripe'
    );
    if (hasPaidTier && usesStripeCheckout) {
      setStripeCheckBusy(true);
      try {
        const token = localStorage.getItem('session_token');
        const sres = await axios.get(`${API}/stripe-connect/status`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const ok = sres.data?.connected && sres.data?.onboarding_complete;
        if (!ok) {
          setShowStripeConnectModal(true);
          setStripeCheckBusy(false);
          return;
        }
      } catch (err) {
        // Network/auth error → show the modal as the safer default. The
        // existing "Cancel" button lets the host bypass to fix things.
        setShowStripeConnectModal(true);
        setStripeCheckBusy(false);
        return;
      }
      setStripeCheckBusy(false);
    }

    // Phase 2E + 2F + 2G: filter by signature so condition changes re-fire
    const guardrails = getPublishGuardrails(form).filter((g) => !dismissedGuardrailSignatures.has(g.signature));
    if (guardrails.length > 0) {
      setShowPublishGuardrailsModal(true);
      return;
    }
    await _doSaveEvent();
  };

  if (loading || !form) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="border-b border-[#1A1A1A] bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Button onClick={() => navigate('/dashboard')} variant="ghost" className="mb-4 -ml-2 text-[#AFAFAF] hover:text-[#EAE7E1]" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h1 className="text-3xl font-bold text-[#EAE7E1] tracking-tight">Edit Event</h1>
            {/* ─── Phase 2G: Dismissed Guardrails chip ─── */}
            {dismissedGuardrailSignatures.size > 0 && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowDismissedPopover((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#1A1A1A] bg-[#0A0A0A] px-3 py-1 text-[11px] text-[#AFAFAF] hover:text-[#EAE7E1] hover:border-[#333]"
                  data-testid="dismissed-guardrails-chip"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></span>
                  {dismissedGuardrailSignatures.size} item{dismissedGuardrailSignatures.size === 1 ? '' : 's'} dismissed
                </button>
                {showDismissedPopover && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowDismissedPopover(false)}
                      aria-hidden="true"
                    />
                    <div
                      className="absolute right-0 top-full mt-2 z-50 w-64 rounded-sm border border-[#1A1A1A] bg-[#0A0A0A] shadow-xl p-3"
                      data-testid="dismissed-guardrails-popover"
                    >
                      <div className="text-[10px] font-bold uppercase tracking-wider text-[#777] mb-2">
                        Dismissed this session
                      </div>
                      <ul className="space-y-1 mb-3 max-h-40 overflow-y-auto">
                        {Array.from(dismissedGuardrailSignatures).map((sig) => (
                          <li
                            key={sig}
                            className="text-xs text-[#EAE7E1] truncate"
                            data-testid="dismissed-guardrails-item"
                          >
                            • {dismissedTitlesBySig[sig] || sig.split('|')[0]}
                          </li>
                        ))}
                      </ul>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          setDismissedGuardrailSignatures(new Set());
                          setDismissedTitlesBySig({});
                          setShowDismissedPopover(false);
                          toast.success('Dismissed guardrails restored');
                        }}
                        className="w-full bg-[#1A1A1A] hover:bg-[#222] text-[#EAE7E1] rounded-sm h-8 text-[11px] uppercase tracking-wider font-semibold"
                        data-testid="dismissed-guardrails-restore"
                      >
                        Restore
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* ─── Post-Guided-Builder free-pricing banner (dismissible) ───── */}
        {form.guided_created
          && (() => {
            const entryTiers = (form.pass_tiers || []).filter(t => (t.type || 'entry') !== 'addon');
            return entryTiers.length > 1 && entryTiers.every(t => (parseFloat(t.price) || 0) === 0);
          })()
          && !freePricingBannerDismissed && (
            <div
              className="mb-6 rounded-sm border border-[#D4AF37]/40 bg-[#D4AF37]/5 px-4 py-3 flex items-start gap-3"
              data-testid="guided-free-pricing-banner"
            >
              <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#EAE7E1] font-medium">
                  Suggested tiers were added.
                </p>
                <p className="text-xs text-[#B9B1A6] mt-0.5">
                  Review pricing and capacity before publishing.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFreePricingBannerDismissed(true)}
                className="text-[#AFAFAF] hover:text-[#EAE7E1] flex-shrink-0"
                data-testid="guided-free-pricing-banner-dismiss"
                aria-label="Dismiss"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="space-y-8"
          data-testid="edit-event-form"
          onInvalid={(e) => {
            // Native HTML5 validation blocked submit. The default tooltip
            // appears at the field — which can be offscreen if the user
            // clicked Save from a section far below. Scroll the first
            // invalid field into view and surface a clear toast.
            setValidationNonce((n) => n + 1);
            if (invalidToastShownRef.current) return;
            invalidToastShownRef.current = true;
            const first = e.target;
            if (first && typeof first.scrollIntoView === 'function') {
              first.scrollIntoView({ behavior: 'smooth', block: 'center' });
              try { first.focus({ preventScroll: true }); } catch (_) {}
            }
            // Friendly label lookup: prefer htmlFor label, then nearest preceding
            // <Label>, then parent wrapper's first <label>, then testid, then name.
            const lookupLabel = (el) => {
              if (!el) return 'required field';
              if (el.labels && el.labels[0]?.innerText) return el.labels[0].innerText;
              const prev = el.closest('div')?.querySelector('label');
              if (prev?.innerText) return prev.innerText;
              return el.name || el.getAttribute?.('data-testid') || 'required field';
            };
            const label = lookupLabel(first);
            toast.error(`Please fill in: ${String(label).replace(/\s*\*$/, '').trim()}`);
            setTimeout(() => { invalidToastShownRef.current = false; }, 1500);
          }}
        >
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-6" data-testid="basic-info-section">
            <h2 className="text-xl font-semibold text-[#EAE7E1] mb-6 tracking-tight">Basic Information</h2>
            <div className="space-y-4">
              <div><Label>Event Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required className="mt-1" data-testid="event-title-input" /></div>
              <div><Label>Description *</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={4} className="mt-1" data-testid="event-description-input" /></div>
              <div><Label>Event Type *</Label><Input value={form.event_type} onChange={(e) => setForm({ ...form, event_type: e.target.value })} required className="mt-1" data-testid="event-type-input" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className="mt-1" data-testid="event-date-input" /></div>
                <div><Label>Start Time *</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required className="mt-1" data-testid="event-time-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>End Time</Label><Input type="time" value={form.end_time || ''} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1" data-testid="event-end-time-input" />{form.time && form.end_time && form.end_time < form.time && !form.is_multi_day && <p className="text-[10px] text-[#D4AF37] mt-1">Overnight — ends next day</p>}</div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 h-9 text-sm text-[#AFAFAF] cursor-pointer">
                    <input type="checkbox" checked={form.is_multi_day || false} onChange={e => setForm({ ...form, is_multi_day: e.target.checked, end_date: e.target.checked ? (form.end_date || '') : '' })} className="rounded" data-testid="multi-day-toggle" />
                    Multi-day event
                  </label>
                </div>
              </div>
              {form.is_multi_day && (
                <div className="grid grid-cols-2 gap-4">
                  <div><Label>End Date</Label><Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} min={form.date} className="mt-1" data-testid="event-end-date-input" /></div>
                  <div />
                </div>
              )}
              <div><Label>Location *</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} required className="mt-1" data-testid="event-location-input" /></div>

              {/* Location Visibility */}
              <div className="border border-[#1A1A1A] rounded-sm p-4">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-[#EAE7E1] text-sm font-medium">Location Visibility</Label>
                  <button type="button" onClick={() => navigate('/guide#hidden-address')} className="text-[11px] text-[#D4AF37] hover:underline" data-testid="help-location-visibility">What does this mean?</button>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2" data-testid="location-visibility-selector">
                  {[
                    { value: 'public', label: 'Public', desc: 'Shown everywhere' },
                    { value: 'hidden', label: 'Hidden', desc: 'Never shown' },
                    { value: 'revealed_later', label: 'Reveal Later', desc: 'Sent before event' },
                  ].map(opt => (
                    <button key={opt.value} type="button" onClick={() => setForm(prev => ({ ...prev, location_visibility: opt.value }))}
                      className={`p-3 rounded-sm border-2 text-center transition-all ${(form.location_visibility || 'public') === opt.value ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#1A1A1A] hover:border-[#333]'}`}
                      data-testid={`loc-vis-${opt.value}`}>
                      <div className={`text-sm font-semibold ${(form.location_visibility || 'public') === opt.value ? 'text-[#EAE7E1]' : 'text-[#777]'}`}>{opt.label}</div>
                      <div className="text-[10px] text-[#555] mt-0.5">{opt.desc}</div>
                    </button>
                  ))}
                </div>
                {(form.location_visibility === 'revealed_later') && (
                  <div className="mt-4 space-y-3 border-t border-[#1A1A1A] pt-4">
                    <div>
                      <Label className="text-[#EAE7E1] text-sm">Private Location Details *</Label>
                      <p className="text-xs text-[#777] mb-1">Only sent via message — never shown publicly.</p>
                      <Textarea value={form.private_location_details || ''} onChange={e => setForm(prev => ({ ...prev, private_location_details: e.target.value }))} placeholder="Full address, gate code, parking..." rows={2} className="bg-[#050505] border-[#1A1A1A] text-[#EAE7E1]" data-testid="private-location-input" />
                    </div>
                    <div>
                      <Label className="text-[#EAE7E1] text-sm">Reveal Timing</Label>
                      <select value={form.location_reveal_mode || 'manual'} onChange={e => setForm(prev => ({ ...prev, location_reveal_mode: e.target.value }))} className="w-full mt-1 px-3 py-2 bg-[#050505] border border-[#1A1A1A] rounded-sm text-[#EAE7E1]" data-testid="reveal-mode-select">
                        <option value="manual">Send manually</option>
                        <option value="auto_48h">48 hours before event</option>
                        <option value="auto_36h">36 hours before event</option>
                        <option value="auto_24h">24 hours before event</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label>Capacity *</Label>
                <div className="flex items-center gap-3 mt-1">
                  <label className="flex items-center gap-2 text-sm text-[#AFAFAF] whitespace-nowrap" data-testid="event-unlimited-capacity-label">
                    <input
                      type="checkbox"
                      checked={!!form.is_unlimited_capacity}
                      onChange={(e) => setForm({ ...form, is_unlimited_capacity: e.target.checked })}
                      className="rounded border-[#1A1A1A]"
                      data-testid="event-unlimited-capacity-checkbox"
                    />
                    Unlimited
                  </label>
                  {!form.is_unlimited_capacity && (
                    <Input
                      type="number"
                      value={form.capacity}
                      onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                      required
                      min="1"
                      className="flex-1"
                      placeholder="Max attendees"
                      data-testid="event-capacity-input"
                    />
                  )}
                </div>
                {!form.is_unlimited_capacity && (
                  <p className="text-xs text-[#777] mt-1">Attendee-based. Add-ons do NOT affect capacity.</p>
                )}
                {form.is_unlimited_capacity && (
                  <p className="text-xs text-[#777] mt-1">Tier and add-on inventory limits still apply.</p>
                )}
              </div>
              <div><Label>Access Release Method</Label><select value={form.release_method || 'automatic'} onChange={(e) => setForm({ ...form, release_method: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-sm" data-testid="release-method-select"><option value="automatic">Automatic</option><option value="manual">Manual Approval</option></select></div>
            </div>
          </div>

          {/* Iter126 — EditEvent parity with CreateEvent for Smart Entry Flow
              and Source Options. Reuses the same components. Hydration is
              handled by the parent via `form.entry_mode`/`form.entry_flow`/
              `form.source_options` which the /edit endpoint round-trips
              unmasked. iter121 retention contract is preserved because we
              only ever update fields the host actually touches. */}
          <SmartEntryFlowSection form={form} setForm={setForm} />

          <SourceOptionsSection
            value={form.source_options}
            onChange={(next) => setForm({ ...form, source_options: next })}
            onSaveAsDefault={async (current) => {
              try {
                const token = localStorage.getItem('session_token');
                const merged = { ...(savedHostDefaults || {}), source_options: current };
                await axios.put(`${API}/users/host-defaults`, merged, { headers: { Authorization: `Bearer ${token}` } });
                const resp = await axios.get(`${API}/users/host-defaults`, { headers: { Authorization: `Bearer ${token}` } });
                setSavedHostDefaults(resp.data || {});
                toast.success('Source options saved as default');
              } catch {
                toast.error('Could not save defaults');
              }
            }}
          />

          {/* Pass Tiers (Entry + Add-On) */}
          <div className="bg-[#0A0A0A] border border-[#D4AF37]/15 rounded-lg p-6" data-testid="pass-tiers-section-edit">
            <div className="flex justify-between items-center mb-6">
              <div><h2 className="text-xl font-semibold text-[#EAE7E1] tracking-tight">Pass Tiers</h2><p className="text-sm text-[#AFAFAF] mt-1">Entry passes and optional add-ons</p></div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" onClick={() => setForm({ ...form, pass_tiers: [...(form.pass_tiers || []), { id: `tier_${Date.now()}`, tier_name: '', price: 0, quantity_available: 0, is_unlimited: false, attendees_per_pass: 1, description: '', is_comp: false, status_default: 'active', requires_access_code: false, access_code_hash: '', type: 'entry', display_order: (form.pass_tiers || []).length, allow_quantity_selection: true, min_quantity: '', max_quantity: '' }] })} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="add-edit-tier-button"><Plus className="w-4 h-4 mr-2" />Entry Tier</Button>
                <Button type="button" onClick={() => setForm({ ...form, pass_tiers: [...(form.pass_tiers || []), { id: `tier_${Date.now()}`, tier_name: '', price: 0, quantity_available: 0, is_unlimited: false, attendees_per_pass: 1, description: '', is_comp: false, status_default: 'active', requires_access_code: false, access_code_hash: '', type: 'addon', display_order: (form.pass_tiers || []).length, allow_quantity_selection: true, min_quantity: '', max_quantity: '' }] })} variant="outline" className="border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10 rounded-sm" data-testid="edit-add-addon-button"><Plus className="w-4 h-4 mr-2" />Add-On</Button>
                {(() => {
                  const eligible = (form.pass_tiers || []).filter(t => (t.type || 'entry') !== 'addon' && PRESET_TIER_ALLOWLIST.has((t.tier_name || '').trim()));
                  if (eligible.length === 0) return null;
                  return (
                    <Button
                      type="button"
                      onClick={() => {
                        setPresetName(`My ${eligible.length}-Tier Preset`);
                        setShowSavePresetModal(true);
                      }}
                      variant="outline"
                      className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm"
                      data-testid="save-as-preset-button"
                    >
                      <Bookmark className="w-4 h-4 mr-2" />
                      Save as Preset
                    </Button>
                  );
                })()}
              </div>
            </div>
            {(!form.pass_tiers || form.pass_tiers.length === 0) ? (
              <div className="text-center py-6 border border-dashed border-[#1A1A1A] rounded-sm">
                <Layers className="w-8 h-8 mx-auto text-[#1A1A1A] mb-2" />
                <p className="text-[#AFAFAF] text-sm">No pass tiers defined</p>
              </div>
            ) : (
              <div className="space-y-4">
                {(() => {
                  const entryTiers = form.pass_tiers.filter(t => (t.type || 'entry') !== 'addon');
                  const allEntryFree = entryTiers.length > 0 && entryTiers.every(t => (parseFloat(t.price) || 0) === 0);
                  if (!allEntryFree) return null;
                  return (
                    <div
                      className="rounded-sm border border-[#D4AF37]/20 bg-[#D4AF37]/[0.03] px-3 py-2 text-xs text-[#B9B1A6] flex items-center gap-2"
                      data-testid="all-tiers-free-note"
                    >
                      <DollarSign className="w-3.5 h-3.5 text-[#D4AF37] flex-shrink-0" />
                      All tiers are currently free
                    </div>
                  );
                })()}
                {form.pass_tiers.map((tier, idx) => {
                  const isAddon = tier.type === 'addon';
                  const isFree = (parseFloat(tier.price) || 0) === 0;
                  return (
                    <div key={tier.id} className={`border rounded-sm p-4 ${isAddon ? 'border-[#D4AF37]/30 bg-[#D4AF37]/[0.02]' : 'border-[#1A1A1A]'}`} data-testid={`edit-tier-${idx}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 min-w-0">
                          <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded ${isAddon ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#1A1A1A] text-[#AFAFAF]'}`}>{isAddon ? 'Add-On' : 'Entry'}</span>
                          <h3 className="font-medium text-[#EAE7E1] truncate">{tier.tier_name || `Tier ${idx + 1}`}</h3>
                          {isFree && (
                            <span
                              className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#D4AF37]/15 text-[#D4AF37]"
                              data-testid={`edit-tier-free-badge-${idx}`}
                            >
                              Free
                            </span>
                          )}
                        </div>
                        <Button type="button" onClick={() => setForm({ ...form, pass_tiers: form.pass_tiers.filter((_, i) => i !== idx) })} variant="ghost" size="sm" className="text-[#AFAFAF] hover:text-[#C44545] flex-shrink-0"><X className="w-4 h-4" /></Button>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>Type</Label><select value={tier.type || 'entry'} onChange={(e) => updateTier(idx, 'type', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-sm text-sm" data-testid={`edit-tier-type-${idx}`}><option value="entry">Entry Pass</option><option value="addon">Add-On</option></select></div>
                        <div><Label>Name *</Label><Input value={tier.tier_name} onChange={(e) => updateTier(idx, 'tier_name', e.target.value)} className="mt-1" /></div>
                        <div><Label>Price ($)</Label><Input type="number" step="0.01" min="0" value={tier.price} onChange={(e) => updateTier(idx, 'price', e.target.value)} disabled={tier.is_comp} className="mt-1" /></div>
                        <div><Label>Quantity Available</Label><Input type="number" min="0" value={tier.quantity_available} onChange={(e) => updateTier(idx, 'quantity_available', e.target.value)} className="mt-1" /></div>
                        {!isAddon && (
                          <div><Label>Attendees/Pass</Label><Input type="number" min="1" value={tier.attendees_per_pass} onChange={(e) => updateTier(idx, 'attendees_per_pass', e.target.value)} className="mt-1" /></div>
                        )}
                        <div className={isAddon ? '' : 'col-span-2'}><Label>Description</Label><Textarea value={tier.description || ''} onChange={(e) => updateTier(idx, 'description', e.target.value)} rows={2} className="mt-1" /></div>
                        {!isAddon && (
                          <div className="flex items-center space-x-2"><input type="checkbox" checked={tier.is_comp} onChange={(e) => updateTier(idx, 'is_comp', e.target.checked)} className="rounded" /><Label>Complimentary</Label></div>
                        )}
                        <div><Label>Status</Label><select value={tier.status_default} onChange={(e) => updateTier(idx, 'status_default', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-sm text-sm" data-testid={`edit-tier-status-${idx}`}><option value="active">Active</option><option value="hidden">Hidden</option><option value="sold_out">Sold Out</option></select></div>
                        {/* Quantity Control */}
                        <div className="col-span-2 border-t border-[#1A1A1A] pt-3 mt-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <input type="checkbox" checked={tier.allow_quantity_selection !== false} onChange={(e) => updateTier(idx, 'allow_quantity_selection', e.target.checked)} className="rounded" data-testid={`edit-tier-qty-toggle-${idx}`} />
                            <span className="text-sm text-[#AFAFAF]">Guests can choose quantity</span>
                            <span className="text-xs text-[#555]">{tier.allow_quantity_selection === false ? '(fixed at 1, single tap)' : '(adjustable with +/- controls)'}</span>
                          </div>
                          {tier.allow_quantity_selection !== false && (
                            <div className="grid grid-cols-2 gap-3">
                              <div><Label className="text-xs">Min Quantity</Label><Input type="number" min="1" value={tier.min_quantity || ''} onChange={(e) => updateTier(idx, 'min_quantity', e.target.value)} placeholder="1" className="mt-1" data-testid={`edit-tier-min-qty-${idx}`} /></div>
                              <div><Label className="text-xs">Max Quantity</Label><Input type="number" min="1" value={tier.max_quantity || ''} onChange={(e) => updateTier(idx, 'max_quantity', e.target.value)} placeholder="No limit" className="mt-1" data-testid={`edit-tier-max-qty-${idx}`} /></div>
                            </div>
                          )}
                        </div>
                        {!isAddon && (
                          <div className="col-span-2 border-t border-[#1A1A1A] pt-3 mt-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <input type="checkbox" checked={tier.requires_access_code || false} onChange={(e) => updateTier(idx, 'requires_access_code', e.target.checked)} className="rounded" data-testid={`edit-tier-access-toggle-${idx}`} />
                              <span className="text-sm text-[#AFAFAF]">Require Access Code</span>
                            </div>
                            {tier.requires_access_code && (
                              <Input value={tier.access_code_hash || ''} onChange={(e) => updateTier(idx, 'access_code_hash', e.target.value)} placeholder="Enter new code (leave blank to keep existing)" className="mt-1" data-testid={`edit-tier-access-code-${idx}`} />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Payment Methods */}
          <div className="bg-[#0A0A0A] border border-[#3E5F4A]/30 rounded-lg p-6" data-testid="payment-methods-section-edit">
            <div className="flex justify-between items-center mb-6">
              <div><h2 className="text-xl font-semibold text-[#EAE7E1] tracking-tight">Payment Methods</h2><p className="text-sm text-[#AFAFAF] mt-1">How guests pay you (links, Zelle, etc.)</p></div>
              <Button type="button" onClick={addPaymentLink} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" data-testid="add-payment-link-button"><Plus className="w-4 h-4 mr-2" /> Add</Button>
            </div>
            {(form.payment_links || []).length === 0 ? (
              <div className="text-center py-6 border border-dashed border-[#1A1A1A] rounded-sm"><p className="text-[#AFAFAF] text-sm">No payment methods added</p></div>
            ) : (
              <div className="space-y-4">
                {(form.payment_links || []).map((link, index) => {
                  const isLinkType = (link.payment_method_type || 'link') === 'link';
                  return (
                    <div key={link.id} className="border border-[#1A1A1A] rounded-sm p-4" data-testid={`payment-link-${index}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex items-center gap-2.5">
                          <PaymentProviderLogo provider={link.payment_provider || getProviderFromMethod(link.method)} method={link.method} size="md" />
                          <h3 className="font-medium text-[#EAE7E1]">{link.method || `Payment ${index + 1}`}</h3>
                        </div>
                        <Button type="button" onClick={() => removePaymentLink(index)} variant="ghost" size="sm" className="text-[#AFAFAF] hover:text-[#C44545]"><X className="w-4 h-4" /></Button>
                      </div>
                      <div className="space-y-3">
                        <div><Label>Method</Label><select value={link.method} onChange={(e) => updatePaymentLink(index, 'method', e.target.value)} className="w-full mt-1 px-3 py-2 rounded-sm" required><option value="">Select</option>{PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}</select></div>

                        <div>
                          <Label>Payment Type</Label>
                          <div className="flex gap-2 mt-1" data-testid={`edit-payment-type-toggle-${index}`}>
                            <button type="button" onClick={() => updatePaymentLink(index, 'payment_method_type', 'link')} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-sm border-2 text-sm font-semibold transition-all ${isLinkType ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#1A1A1A] text-[#AFAFAF] hover:border-[#333]'}`}>
                              <Link className="w-3 h-3" />Link-Based
                            </button>
                            <button type="button" onClick={() => updatePaymentLink(index, 'payment_method_type', 'direct')} className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-sm border-2 text-sm font-semibold transition-all ${!isLinkType ? 'border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]' : 'border-[#1A1A1A] text-[#AFAFAF] hover:border-[#333]'}`}>
                              <Send className="w-3 h-3" />Direct Transfer
                            </button>
                          </div>
                        </div>

                        {isLinkType ? (
                          <div><Label>Payment URL *</Label><Input value={link.url || ''} onChange={(e) => updatePaymentLink(index, 'url', e.target.value)} placeholder="https://venmo.com/..." required className="mt-1" /></div>
                        ) : (
                          <>
                            <div><Label>Recipient Name</Label><Input value={link.recipient_name || ''} onChange={(e) => updatePaymentLink(index, 'recipient_name', e.target.value)} placeholder="John Smith" className="mt-1" /></div>
                            <div><Label>Email or Phone *</Label><Input value={link.email_or_phone || ''} onChange={(e) => updatePaymentLink(index, 'email_or_phone', e.target.value)} placeholder="john@email.com or (555) 123-4567" required className="mt-1" /></div>
                            <div>
                              <Label>QR Code (Optional)</Label>
                              <div className="flex items-center gap-3 mt-1">
                                <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && handlePaymentQRUpload(e.target.files[0], index)} className="hidden" id={`edit-qr-upload-${index}`} />
                                <Button type="button" variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm text-sm" onClick={() => document.getElementById(`edit-qr-upload-${index}`).click()}><Upload className="w-3 h-3 mr-1" />Upload QR</Button>
                                {link.qr_code_image && <SafeImage src={`${BACKEND_URL}${link.qr_code_image}`} alt="QR" className="h-10 w-10 rounded" imgClassName="bg-white p-0.5 object-contain border border-[#1A1A1A]" fallbackLabel="QR" />}
                              </div>
                            </div>
                            <div><Label>Suggested Memo (Optional)</Label><Input value={link.suggested_memo || ''} onChange={(e) => updatePaymentLink(index, 'suggested_memo', e.target.value)} placeholder="EventName - YourName" className="mt-1" /></div>
                          </>
                        )}

                        <div><Label>Instructions (Optional)</Label><Textarea value={link.instructions || ''} onChange={(e) => updatePaymentLink(index, 'instructions', e.target.value)} rows={2} className="mt-1" /></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Branding */}
          <div className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-6" data-testid="branding-section-edit">
            <h2 className="text-xl font-semibold text-[#EAE7E1] mb-6 tracking-tight">Branding</h2>
            <div className="space-y-6">
              <div>
                <Label>Organizer Logo</Label>
                <p className="text-xs text-[#777] mt-1 mb-2">Your brand, venue, or organization logo.</p>
                <div className="flex items-center space-x-4">
                  <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'logo')} className="hidden" id="logo-upload" />
                  <Button type="button" variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" disabled={uploadingLogo} onClick={() => document.getElementById('logo-upload').click()} data-testid="logo-upload-button"><Upload className="w-4 h-4 mr-2" />{uploadingLogo ? 'Uploading...' : 'Upload'}</Button>
                  {form.organizer_logo_url && (
                    <div className="flex items-center gap-2 p-2 border border-[#C6A66B]/40 rounded-sm bg-[#C6A66B]/5" data-testid="edit-organizer-logo-preview-wrap">
                      <SafeImage src={`${BACKEND_URL}${form.organizer_logo_url}`} alt="Organizer Logo" className="h-12 w-20 rounded" imgClassName="bg-white p-1 object-contain" fallbackLabel="LOGO" />
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#C6A66B] uppercase tracking-wider"><Check className="w-3 h-3" />Uploaded</span>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <Label>Event Flyer / Event Image</Label>
                <p className="text-xs text-[#777] mt-1 mb-2">Specific to this event. Used as the hero image on the public event page.</p>
                <div className="flex items-center space-x-4">
                  <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && handleFileUpload(e.target.files[0], 'image')} className="hidden" id="image-upload" />
                  <Button type="button" variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm" disabled={uploadingImage} onClick={() => document.getElementById('image-upload').click()} data-testid="image-upload-button"><Upload className="w-4 h-4 mr-2" />{uploadingImage ? 'Uploading...' : 'Upload'}</Button>
                  {form.event_image_url && (
                    <div className="flex items-center gap-2 p-2 border border-[#C6A66B]/40 rounded-sm bg-[#C6A66B]/5" data-testid="edit-event-flyer-preview-wrap">
                      <SafeImage src={`${BACKEND_URL}${form.event_image_url}`} alt="Event Flyer" className="h-24 w-40 rounded" imgClassName="object-cover" fallbackLabel="FLYER" />
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#C6A66B] uppercase tracking-wider"><Check className="w-3 h-3" />Uploaded</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Access Control Window */}
          <StaffAccessWindow
            eventDate={form.date}
            eventStartTime={form.time}
            eventEndTime={form.end_time}
            eventIsMultiDay={form.is_multi_day}
            eventEndDate={form.end_date}
            startValue={form.access_start_datetime}
            endValue={form.access_end_datetime}
            onChangeStart={(v) => setForm(prev => ({ ...prev, access_start_datetime: v }))}
            onChangeEnd={(v) => setForm(prev => ({ ...prev, access_end_datetime: v }))}
          />

          {/* Validation Summary + Submit */}
          <div>
            <ValidationSummary formRef={formRef} triggerNonce={validationNonce} />
            <div className="flex justify-end space-x-4">
              <Button type="button" onClick={() => navigate('/dashboard')} variant="outline" className="border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm px-6 py-3" data-testid="cancel-button">Cancel</Button>
              <Button type="submit" className="bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-6 py-3 font-bold" disabled={saving || stripeCheckBusy} data-testid="save-event-button">{saving ? 'Saving...' : (stripeCheckBusy ? 'Checking Stripe…' : 'Save Changes')}</Button>
            </div>
          </div>
        </form>

        {/* ─── P2.3B: Stripe Connect publish guardrail modal ─── */}
        {showStripeConnectModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4" data-testid="stripe-connect-publish-modal">
            <div
              className="absolute inset-0 bg-black/70 backdrop-blur-sm"
              onClick={() => setShowStripeConnectModal(false)}
              aria-hidden="true"
            />
            <div className="relative w-full max-w-md bg-[#0A0A0A] border border-[#5f4a1f] rounded-sm p-6">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-[#1f1a0d] border border-[#5f4a1f] flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-[#FFB800]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-[#EAE7E1]" data-testid="stripe-connect-modal-title">
                    Connect Stripe to Accept Payments
                  </h2>
                  <p className="text-sm text-[#AFAFAF] mt-2 leading-relaxed" data-testid="stripe-connect-modal-body">
                    You need to connect your Stripe account before publishing paid events. It only takes a few minutes.
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 mt-5">
                <Button
                  onClick={() => navigate('/settings/payments')}
                  className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm px-4 py-2.5 font-bold"
                  data-testid="stripe-connect-modal-go-button"
                >
                  Connect Stripe
                </Button>
                <Button
                  onClick={() => setShowStripeConnectModal(false)}
                  variant="outline"
                  className="flex-1 border-[#1A1A1A] text-[#AFAFAF] hover:text-[#EAE7E1] hover:bg-[#1A1A1A] rounded-sm px-4 py-2.5"
                  data-testid="stripe-connect-modal-cancel-button"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Phase 2E + 2F: Unified "Before You Publish" guardrails modal ─── */}
        {showPublishGuardrailsModal && (() => {
          const allGuardrails = getPublishGuardrails(form);
          const visibleGuardrails = allGuardrails.filter((g) => !dismissedGuardrailSignatures.has(g.signature));

          // Edge case: if everything is dismissed by the time the modal renders
          // (rare race, but cleanly handled), close + save next tick.
          if (visibleGuardrails.length === 0) {
            setTimeout(() => {
              setShowPublishGuardrailsModal(false);
              _doSaveEvent();
            }, 0);
            return null;
          }

          const dismissOne = (g) => {
            setDismissedTitlesBySig((prev) => ({ ...prev, [g.signature]: g.title }));
            setDismissedGuardrailSignatures((prev) => {
              const next = new Set(prev);
              next.add(g.signature);
              const stillVisible = allGuardrails.filter((x) => !next.has(x.signature));
              if (stillVisible.length === 0) {
                setShowPublishGuardrailsModal(false);
                _doSaveEvent();
              }
              return next;
            });
          };

          const publishAnyway = () => {
            const titlePatch = {};
            for (const g of visibleGuardrails) titlePatch[g.signature] = g.title;
            setDismissedTitlesBySig((prev) => ({ ...prev, ...titlePatch }));
            setDismissedGuardrailSignatures((prev) => {
              const next = new Set(prev);
              for (const g of visibleGuardrails) next.add(g.signature);
              return next;
            });
            setShowPublishGuardrailsModal(false);
            _doSaveEvent();
          };

          const anchorScroll = (anchor) => {
            setShowPublishGuardrailsModal(false);
            if (!anchor || typeof window === 'undefined') return;
            try {
              const map = {
                description: 'textarea',
                tiers: '[data-testid="pass-tiers-section-edit"]',
                location: '[data-testid="location-field"], #location',
                payment: '[data-testid="payment-methods-section"], [data-testid="payment-links-section"]',
              };
              const sel = map[anchor];
              const el = sel ? document.querySelector(sel) : null;
              if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            } catch (_) { /* no-op */ }
          };

          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4 py-6 overflow-y-auto"
              onClick={() => !saving && setShowPublishGuardrailsModal(false)}
              data-testid="publish-guardrails-modal"
            >
              <div
                className="bg-[#0A0A0A] border-2 border-[#D4AF37] rounded-lg p-6 max-w-md w-full my-auto"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start gap-3 mb-2">
                  <AlertCircle className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <h3 className="text-lg font-bold text-[#EAE7E1]" data-testid="publish-guardrails-modal-title">
                      Before You Publish
                    </h3>
                    <p className="text-xs text-[#B9B1A6] mt-0.5">
                      Review these items before making your event live.
                    </p>
                  </div>
                </div>
                <div className="space-y-2.5 my-4 max-h-[55vh] overflow-y-auto pr-1">
                  {visibleGuardrails.map((g) => {
                    const isWarn = g.severity === 'warning';
                    const Icon = isWarn ? AlertCircle : Info;
                    return (
                      <div
                        key={g.id}
                        className={`rounded-sm border p-3 ${isWarn ? 'border-[#D4AF37]/40 bg-[#D4AF37]/5' : 'border-[#1A1A1A] bg-[#0A0A0A]'}`}
                        data-testid={`publish-guardrail-${g.id}`}
                      >
                        <div className="flex items-start gap-2 mb-1">
                          <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isWarn ? 'text-[#D4AF37]' : 'text-[#5EC8FF]'}`} />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-semibold text-[#EAE7E1]">{g.title}</span>
                              <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${isWarn ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-[#5EC8FF]/15 text-[#5EC8FF]'}`}>
                                {g.severity}
                              </span>
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-[#B9B1A6] ml-6 leading-relaxed">{g.message}</p>
                        <div className="ml-6 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
                          {g.actionLabel && (
                            <button
                              type="button"
                              onClick={() => anchorScroll(g.anchor)}
                              className="text-[11px] uppercase tracking-wider font-semibold text-[#D4AF37] hover:text-[#F2D27A]"
                              data-testid={`publish-guardrail-${g.id}-action`}
                            >
                              {g.actionLabel} →
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => dismissOne(g)}
                            className="text-[11px] uppercase tracking-wider text-[#777] hover:text-[#AFAFAF]"
                            data-testid={`publish-guardrail-${g.id}-dismiss`}
                          >
                            Dismiss
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    onClick={() => setShowPublishGuardrailsModal(false)}
                    disabled={saving}
                    className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] hover:opacity-90 text-[#050505] rounded-sm font-bold"
                    data-testid="publish-guardrails-review-items"
                  >
                    Review Items
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={publishAnyway}
                    disabled={saving}
                    className="flex-1 border-[#333] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm"
                    data-testid="publish-guardrails-publish-anyway"
                  >
                    {saving ? 'Saving...' : 'Publish Anyway'}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ─── Phase 2D: Save Tier Preset modal ─── */}
        {showSavePresetModal && (() => {
          const eligible = (form.pass_tiers || []).filter(
            t => (t.type || 'entry') !== 'addon' && PRESET_TIER_ALLOWLIST.has((t.tier_name || '').trim())
          );
          const handleSave = async () => {
            const trimmed = (presetName || '').trim();
            if (!trimmed) { toast.error('Preset name is required'); return; }
            setSavingPreset(true);
            try {
              const token = localStorage.getItem('session_token');
              const tiersPayload = eligible.map(t => ({
                name: (t.tier_name || '').trim(),
                attendee_count: t.tier_name === 'Couples Access' ? 2 : t.tier_name === 'Group Pass' ? 4 : 1,
              }));
              await axios.post(
                `${API}/users/tier-presets`,
                { name: trimmed, tiers: tiersPayload },
                { headers: { Authorization: `Bearer ${token}` } }
              );
              toast.success('Preset saved');
              setShowSavePresetModal(false);
              setPresetName('');
            } catch (e) {
              const detail = e.response?.data?.detail;
              toast.error(typeof detail === 'string' ? detail : 'Failed to save preset');
            } finally {
              setSavingPreset(false);
            }
          };
          return (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4"
              onClick={() => !savingPreset && setShowSavePresetModal(false)}
              data-testid="save-preset-modal"
            >
              <div
                className="bg-[#0A0A0A] border-2 border-[#D4AF37] rounded-lg p-6 max-w-md w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start gap-3 mb-3">
                  <Bookmark className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <h3 className="text-lg font-bold text-[#EAE7E1]" data-testid="save-preset-modal-title">
                    Save Tier Preset
                  </h3>
                </div>
                <p className="text-xs text-[#777] mb-3">
                  Saves only tier names and attendee counts — pricing and codes are excluded so you can reuse this on any event.
                </p>
                <Input
                  value={presetName}
                  onChange={(e) => setPresetName(e.target.value)}
                  placeholder="My Preset"
                  maxLength={60}
                  className="h-11 text-base mb-3"
                  data-testid="save-preset-name-input"
                />
                <div className="text-[11px] text-[#AFAFAF] mb-4 leading-relaxed" data-testid="save-preset-tier-list">
                  Will save <span className="text-[#D4AF37] font-semibold">{eligible.length}</span> tier{eligible.length === 1 ? '' : 's'}: {eligible.map(t => t.tier_name).join(', ')}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    type="button"
                    onClick={() => setShowSavePresetModal(false)}
                    variant="outline"
                    disabled={savingPreset}
                    className="flex-1 border-[#333] text-[#AFAFAF] hover:text-[#EAE7E1] rounded-sm"
                    data-testid="save-preset-cancel"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    onClick={handleSave}
                    disabled={savingPreset || !presetName.trim() || eligible.length === 0}
                    className="flex-1 bg-gradient-to-r from-[#8C6B1F] via-[#D4AF37] to-[#F2D27A] text-[#050505] rounded-sm font-bold"
                    data-testid="save-preset-confirm"
                  >
                    {savingPreset ? 'Saving...' : 'Save Preset'}
                  </Button>
                </div>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
